import { Dayjs } from "dayjs";
import {
  createContext,
  RefObject,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useVideoControlState } from "./VideoControlContext";

const MIN_ZOOM_LEVEL = 1; // 100% - full view
const MIN_VISIBLE_DURATION_SECONDS = 60; // 1 minute minimum visible duration
const ZOOM_SPEED = 1.2; // 20% per scroll
const ZOOM_THROTTLE_MS = 16; // ~60fps for smooth zooming

interface TimelineZoomContextValue {
  duration: number;
  visibleStart: Dayjs | undefined;
  visibleEnd: Dayjs | undefined;
  handleZoom: (deltaY: number, cursorTimeSeconds?: number) => void;
  resetZoom: () => void;
  isZoomed: boolean;
}

const TimelineZoomContext = createContext<TimelineZoomContextValue | null>(
  null
);

interface TimelineZoomProviderProps {
  fullDuration: number;
  videoStart: Dayjs | undefined;
  minVisibleDurationSeconds?: number;
  children: ReactNode;
}

// Must be rendered inside a VideoControlProvider: the CurrentTimeRefSync leaf
// subscribes to useVideoControlState() to keep currentTimeRef live without
// re-rendering this provider. The VideoTimeline.tsx fallback path also relies
// on this — callers rendering VideoTimeline with disableZoom=false need to be
// inside a VideoControlProvider.
export const TimelineZoomProvider = ({
  fullDuration,
  videoStart,
  minVisibleDurationSeconds = MIN_VISIBLE_DURATION_SECONDS,
  children,
}: TimelineZoomProviderProps) => {
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM_LEVEL);
  const [viewStart, setViewStart] = useState(0);

  const currentTimeRef = useRef(0);

  const lastZoomTimeRef = useRef(0);
  const pendingZoomRef = useRef<{
    deltaY: number;
    cursorTimeSeconds?: number;
  } | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const applyZoom = useCallback(
    (deltaY: number, cursorTimeSeconds?: number) => {
      const prevZoom = zoomLevel;
      const prevDuration = fullDuration / prevZoom;

      // Calculate max zoom based on minimum visible duration
      const maxZoomLevel = fullDuration / minVisibleDurationSeconds;

      const zoomFactor = deltaY < 0 ? ZOOM_SPEED : 1 / ZOOM_SPEED;
      const newZoom = Math.max(
        MIN_ZOOM_LEVEL,
        Math.min(maxZoomLevel, prevZoom * zoomFactor)
      );

      if (newZoom === prevZoom) {
        return;
      }

      const newDuration = fullDuration / newZoom;
      const pivotTime = cursorTimeSeconds ?? currentTimeRef.current;
      const pivotRatio = (pivotTime - viewStart) / prevDuration;
      const newViewStart = pivotTime - pivotRatio * newDuration;

      setZoomLevel(newZoom);
      setViewStart(newViewStart);
    },
    [zoomLevel, fullDuration, minVisibleDurationSeconds, viewStart]
  );

  const handleZoom = useCallback(
    (deltaY: number, cursorTimeSeconds?: number) => {
      const now = Date.now();
      const timeSinceLastZoom = now - lastZoomTimeRef.current;

      pendingZoomRef.current = { deltaY, cursorTimeSeconds };

      if (timeSinceLastZoom < ZOOM_THROTTLE_MS) {
        animationFrameRef.current ??= window.requestAnimationFrame(() => {
          animationFrameRef.current = null;
          if (pendingZoomRef.current) {
            const { deltaY: d, cursorTimeSeconds: c } = pendingZoomRef.current;
            pendingZoomRef.current = null;
            applyZoom(d, c);
            lastZoomTimeRef.current = Date.now();
          }
        });
      } else {
        if (animationFrameRef.current !== null) {
          window.cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        pendingZoomRef.current = null;
        applyZoom(deltaY, cursorTimeSeconds);
        lastZoomTimeRef.current = Date.now();
      }
    },
    [applyZoom]
  );

  const resetZoom = useCallback(() => {
    setZoomLevel(MIN_ZOOM_LEVEL);
    setViewStart(0);
  }, []);

  const visibleDuration = fullDuration / zoomLevel;

  const visibleStartSeconds = useMemo(() => {
    if (viewStart < 0) {
      return 0;
    } else if (viewStart + visibleDuration > fullDuration) {
      return Math.max(0, fullDuration - visibleDuration);
    }
    return viewStart;
  }, [viewStart, visibleDuration, fullDuration]);

  useEffect(() => {
    if (viewStart !== visibleStartSeconds) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- clamps viewStart to valid range after zoom changes
      setViewStart(visibleStartSeconds);
    }
  }, [visibleStartSeconds, viewStart]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const visibleStart = useMemo(
    () =>
      videoStart ? videoStart.add(visibleStartSeconds, "second") : undefined,
    [videoStart, visibleStartSeconds]
  );

  const visibleEndSeconds = Math.min(
    fullDuration,
    visibleStartSeconds + visibleDuration
  );
  const visibleEnd = useMemo(
    () =>
      videoStart ? videoStart.add(visibleEndSeconds, "second") : undefined,
    [videoStart, visibleEndSeconds]
  );

  const value = useMemo(
    () => ({
      duration: visibleDuration,
      visibleStart,
      visibleEnd,
      handleZoom,
      resetZoom,
      isZoomed: zoomLevel > MIN_ZOOM_LEVEL,
    }),
    [
      visibleDuration,
      visibleStart,
      visibleEnd,
      handleZoom,
      resetZoom,
      zoomLevel,
    ]
  );

  useHotkeys(
    "mod+0",
    () => {
      resetZoom();
    },
    { enableOnFormTags: false, scopes: ["timelineZoom"] },
    [resetZoom]
  );

  return (
    <TimelineZoomContext value={value}>
      <CurrentTimeRefSync currentTimeRef={currentTimeRef} />
      {children}
    </TimelineZoomContext>
  );
};

// Mirrors VideoControl currentTime into the zoom provider's ref without
// re-rendering the provider or its other children on every timeupdate.
const CurrentTimeRefSync = ({
  currentTimeRef,
}: {
  currentTimeRef: RefObject<number>;
}) => {
  const { currentTime } = useVideoControlState();
  currentTimeRef.current = currentTime;
  return null;
};

export const useTimelineZoom = (): TimelineZoomContextValue | null => {
  return use(TimelineZoomContext);
};

interface UseTimelineWheelZoomResult {
  onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
}

export const useTimelineWheelZoom = (
  timelineRef: RefObject<HTMLDivElement | null>,
  videoStart: Dayjs | undefined,
  enabled = true
): UseTimelineWheelZoomResult => {
  const zoomContext = useTimelineZoom();
  const handleZoom = enabled ? zoomContext?.handleZoom : undefined;
  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleDuration = zoomContext?.duration;

  const visibleStartSeconds =
    visibleStart && videoStart ? visibleStart.diff(videoStart, "second") : 0;

  const cursorTimeRef = useRef<number | undefined>(undefined);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!timelineRef.current || visibleDuration === undefined) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const relativePosition = Math.max(0, Math.min(1, x / rect.width));
      cursorTimeRef.current =
        visibleStartSeconds + relativePosition * visibleDuration;
    },
    [timelineRef, visibleStartSeconds, visibleDuration]
  );

  const onMouseLeave = useCallback(() => {
    cursorTimeRef.current = undefined;
  }, []);

  useEffect(() => {
    const element = timelineRef.current;
    if (!element || !handleZoom) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        const delta = event.deltaY !== 0 ? event.deltaY : event.deltaX;
        handleZoom(delta, cursorTimeRef.current);
      }
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [timelineRef, handleZoom]);

  return { onMouseMove, onMouseLeave };
};
